import api from 'apis/restaurant-api/api';
import * as apiUtils from 'apis/restaurant-api/utils';

import {
  TranslationOperation,
  Menu,
  Item,
  Option,
  MenuContainer,
  CategoryUpdatePayload,
  CategoryCreatePayload,
  ItemBindingCreatePayload,
  ItemCreatePayload,
  ItemUpdatePayload,
  OptionBindingCreatePayload,
  OptionBindingUpdatePayload,
  OptionCreatePayload,
  OptionUpdatePayload,
  Version,
  ExportedMenu,
  ImportMenuPayload,
  Product,
  ProductInformationSettings,
  SyncResult,
  MenuConfigs,
  BulkEditPayload,
  BulkCreateWoltSpecialsPayload,
  BulkActionBasePayload,
  ItemCommission,
  BulkChangeCategoryPayload,
  SubcategoryCreatePayload,
  BulkUpdateSpecialsPeriodPayload,
} from './types';

export const readMenu = (menuId: string): Promise<Menu> =>
  api
    .get<Menu>(`/v2/merchant-admin/menus/${menuId}`, {
      params: {
        with_subcategories: true,
      },
    })
    .then(response => response.data);

export const readMenuAll = (menuId: string): Promise<MenuContainer> =>
  api.get<MenuContainer>(`/v2/merchant-admin/menus/${menuId}/all`).then(response => response.data);

export const createMenu = (primaryLanguage: Language, venueId: string): Promise<Menu> =>
  api
    .post<Menu>(`/v2/merchant-admin/venues/${venueId}/menu`, {
      primary_language: primaryLanguage,
    })
    .then(response => response.data);

export const deleteMenu = (menuId: string) =>
  api.delete(`/v2/merchant-admin/menus/${menuId}`).then(response => response.data);

export const updateCategory = (menuId: string, _version: Version, data: CategoryUpdatePayload): Promise<Menu> =>
  api.put<Menu>(`/v2/merchant-admin/menus/${menuId}/categories/${data.id}`, data).then(response => response.data);

export const deleteCategory = (menuId: string, categoryId: string): Promise<Menu> =>
  api.delete(`/v2/merchant-admin/menus/${menuId}/categories/${categoryId}`).then(response => response.data as Menu);

export const createItemBinding = (
  menuId: string,
  categoryId: string,
  itemBinding: ItemBindingCreatePayload,
): Promise<Menu> =>
  api
    .post<Menu>(`/v2/merchant-admin/menus/${menuId}/categories/${categoryId}/item-bindings`, itemBinding)
    .then(response => response.data);

export const deleteItemBinding = (menuId: string, categoryId: string, itemBindingId: string): Promise<Menu> =>
  api
    .delete(`/v2/merchant-admin/menus/${menuId}/categories/${categoryId}/item-bindings/${itemBindingId}`)
    .then(response => response.data as Menu);

export const getItem = (menuId: string, itemId: string): Promise<Item> =>
  api.get<Item>(`/v2/merchant-admin/${menuId}/items/${itemId}`).then(response => response.data);

export const updateItem = (menuId: string, itemId: string, data: ItemUpdatePayload): Promise<Item> =>
  api.put<Item>(`/v2/merchant-admin/${menuId}/items/${itemId}`, data).then(response => response.data);

export const updateItemEnabled = (menuId: string, itemId: string, enabled: boolean): Promise<Item> =>
  api
    .put<Item>(`/v2/merchant-admin/${menuId}/items/${itemId}/enabled`, { value: enabled })
    .then(response => response.data);

export const updateCategoriesOrder = (menuId: string, ids: string[]): Promise<Menu> =>
  api.put<Menu>(`/v2/merchant-admin/menus/${menuId}/categories/order`, ids).then(response => response.data);

export const updateSubcategoriesOrder = (menuId: string, categoryId: string, ids: string[]): Promise<Menu> =>
  api
    .put<Menu>(`/v2/merchant-admin/menus/${menuId}/categories/${categoryId}/order-subcategories`, { ordering: ids })
    .then(response => response.data);

export const changeSubcategoryCategory = (menuId: string, subcategoryId: string, categoryId: string): Promise<Menu> =>
  api
    .put<Menu>(`/v2/merchant-admin/menus/${menuId}/categories/${subcategoryId}/assign-parent-category`, {
      parent_category_id: categoryId,
    })
    .then(response => response.data);

export const updateItemBindingsOrder = (menuId: string, categoryId: string, ids: string[]): Promise<Menu> =>
  api
    .put<Menu>(`/v2/merchant-admin/menus/${menuId}/categories/${categoryId}/item-bindings/order`, ids)
    .then(response => response.data);

export const patchItemBindingsOrder = (
  menuId: string,
  categoryId: string,
  payload: { id: string; order: number }[],
): Promise<Menu> =>
  api
    .patch<Menu>(`/v2/merchant-admin/menus/${menuId}/categories/${categoryId}/item-bindings/order`, payload)
    .then(response => response.data);

export const deleteItem = (menuId: string, itemId: string) =>
  api.delete(`/v2/merchant-admin/${menuId}/items/${itemId}`).then(response => response.data as unknown);

export const createItem = (menuId: string, data: ItemCreatePayload): Promise<Item> =>
  api.post<Item>(`/v2/merchant-admin/${menuId}/items`, data).then(response => response.data);

export const getProductFromSource = async (
  menuId: string,
  barcodeGtin: string,
  catalogCountry: string,
): Promise<Product> =>
  api
    .get<Product>(`/v2/merchant-admin/menus/${menuId}/products`, {
      params: { gtin: barcodeGtin, catalog_country: catalogCountry },
    })
    .then(response => response.data);

export const getProductInformationSettings = async (menuId: string): Promise<ProductInformationSettings> =>
  api
    .get<ProductInformationSettings>(`/v2/merchant-admin/menus/${menuId}/product-information-settings`)
    .then(response => response.data);

export const createOptionBinding = (menuId: string, itemId: string, data: OptionBindingCreatePayload): Promise<Item> =>
  api.post<Item>(`/v2/merchant-admin/${menuId}/items/${itemId}/option-bindings`, data).then(response => response.data);

export const updateOptionBinding = (
  menuId: string,
  itemId: string,
  _version: Version,
  optionBindingId: string,
  data: OptionBindingUpdatePayload,
): Promise<Item> =>
  api
    .put<Item>(`/v2/merchant-admin/${menuId}/items/${itemId}/option-bindings/${optionBindingId}`, data)
    .then(response => response.data);

export const deleteOptionBinding = (menuId: string, itemId: string, optionBindingId: string): Promise<Item> =>
  api
    .delete(`/v2/merchant-admin/${menuId}/items/${itemId}/option-bindings/${optionBindingId}`)
    .then(response => response.data as Item);

export const updateOptionBindingsOrder = (menuId: string, itemId: string, ids: string[]): Promise<Item> =>
  api
    .put<Item>(`/v2/merchant-admin/${menuId}/items/${itemId}/option-bindings/order`, ids)
    .then(response => response.data);

export const updateCategoryTranslations = (
  menuId: string,
  categoryId: string,
  version: number,
  operations: TranslationOperation[],
): Promise<Menu> =>
  api
    .post<Menu>(`/v2/merchant-admin/menus/${menuId}/categories/${categoryId}/translations`, {
      version,
      operations,
    })
    .then(response => response.data);

export const updateItemTranslations = (
  menuId: string,
  itemId: string,
  version: number,
  operations: TranslationOperation[],
): Promise<Item> =>
  api
    .post<Item>(`/v2/merchant-admin/${menuId}/items/${itemId}/translations`, {
      version,
      operations,
    })
    .then(response => response.data);

export const createOption = (menuId: string, data: OptionCreatePayload): Promise<Option> =>
  api.post<Option>(`/v2/merchant-admin/${menuId}/options`, data).then(response => response.data);

export const updateOption = (menuId: string, optionId: string, data: OptionUpdatePayload): Promise<Option> =>
  api.put<Option>(`/v2/merchant-admin/${menuId}/options/${optionId}`, data).then(response => response.data);

export const deleteOption = (menuId: string, optionId: string): Promise<MenuContainer> =>
  api.delete(`/v2/merchant-admin/${menuId}/options/${optionId}`).then(response => response.data as MenuContainer);

export const updateOptionBindingTranslations = (
  menuId: string,
  itemId: string,
  bindingId: string,
  version: number,
  operations: TranslationOperation[],
): Promise<Item> =>
  api
    .post<Item>(`/v2/merchant-admin/${menuId}/items/${itemId}/option-bindings/${bindingId}/translations`, {
      version,
      operations,
    })
    .then(response => response.data);

export const updateOptionTranslations = (
  menuId: string,
  optionId: string,
  version: number,
  operations: TranslationOperation[],
): Promise<Option> =>
  api
    .post<Option>(`/v2/merchant-admin/${menuId}/options/${optionId}/translations`, {
      version,
      operations,
    })
    .then(response => response.data);

export const updateOptionValueTranslations = (
  menuId: string,
  optionId: string,
  valueId: string,
  version: number,
  operations: TranslationOperation[],
): Promise<Option> =>
  api
    .post<Option>(`/v2/merchant-admin/${menuId}/options/${optionId}/values/${valueId}/translations`, {
      version,
      operations,
    })
    .then(response => response.data);

export const updateOptionValueEnabled = (menuId: string, optionId: string, valueId: string, value: boolean) =>
  api
    .put<Option>(`/v2/merchant-admin/${menuId}/options/${optionId}/values/${valueId}/enabled`, { value })
    .then(response => response.data);

export const deleteOptionChoice = (menuId: string, optionId: string, valueId: string) =>
  api.delete(`/v2/merchant-admin/${menuId}/options/${optionId}/values/${valueId}`).then(response => response.data);

export const deleteTranslations = (menuId: string, language: Language): Promise<MenuContainer> =>
  api
    .delete(`/v2/merchant-admin/menus/${menuId}/translations/${language}`)
    .then(response => response.data as MenuContainer);

export const updatePrimaryLanguage = (menuId: string, version: number, language: Language): Promise<Menu> =>
  api
    .put<Menu>(`/v2/merchant-admin/menus/${menuId}/primary_language`, { lang: language, version })
    .then(response => response.data);

export const exportMenu = (menuId: string, params?: { when?: string }): Promise<ExportedMenu> =>
  api.get<ExportedMenu>(`/v2/merchant-admin/menus/${menuId}/export`, { params }).then(response => response.data);

export const importMenu = (
  menuId: string,
  payload: ImportMenuPayload,
  keepPriceInfo: boolean,
  keepAvailabilityInfo: boolean,
): Promise<MenuContainer> =>
  api
    .post<MenuContainer>(
      `/v2/merchant-admin/menus/${menuId}/import?inspect_price_fields=${!keepPriceInfo}&inspect_availability_fields=${!keepAvailabilityInfo}`,
      {
        ...payload,
        menu_id: menuId,
      },
    )
    .then(response => response.data);

const fetchMenuImageUrls = (menuId: string, filename: string, extraData: Record<string, any> = {}) =>
  api
    .post<{
      cdn_url: string;
      upload_url: string;
    }>(`/v2/merchant-admin/menus/${menuId}/upload-image`, {
      original_filename: filename,
      ...extraData,
    })
    .then(response => response.data);

export const uploadMenuImage = async (menuId: string, file: File, extraData: Record<string, any> = {}) => {
  const { cdn_url, upload_url } = await fetchMenuImageUrls(menuId, file.name, extraData);
  await apiUtils.uploadFileToUrl(file, upload_url);
  return cdn_url;
};

export const getMenuConfigs = async (menuId: string) =>
  api.get<MenuConfigs>(`/v2/merchant-admin/menus/${menuId}/configs`).then(res => res.data);

export const createMenuFromCsv = async (params: {
  menuId: string;
  csvFile: File;
  categoryId?: string;
  catalogCountry?: string;
}): Promise<SyncResult> => {
  const { menuId, csvFile, categoryId, catalogCountry } = params;
  const formData = new FormData();
  formData.append('items', csvFile);
  return api
    .post<SyncResult>(`/v2/merchant-admin/menus/${menuId}/csv`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      params: { category_id: categoryId, catalog_country: catalogCountry },
    })
    .then(response => response.data);
};

export const bulkDeleteItems = async (menuId: string, payload: BulkActionBasePayload): Promise<MenuContainer> =>
  api.post(`/v2/merchant-admin/menus/${menuId}/items/batch-delete`, payload).then(response => response.data);

export const bulkEditItems = async (menuId: string, payload: BulkEditPayload): Promise<MenuContainer> =>
  api
    .post(`/v2/merchant-admin/menus/${menuId}/items/batch-modify`, {
      ...payload,
    })
    .then(response => response.data);

export const bulkChangeCategory = async (menuId: string, payload: BulkChangeCategoryPayload): Promise<Menu> =>
  api
    .post(`/v2/merchant-admin/menus/${menuId}/items/batch-change-category`, {
      ...payload,
    })
    .then(response => response.data);

export const bulkUpdateItems = (menuId: string, data: ItemUpdatePayload[]) =>
  api
    .post<{
      failed: Record<string, string>;
      succeeded: string[];
    }>(`/v2/merchant-admin/menus/${menuId}/items/batch-update`, { items: data })
    .then(response => response.data);

export const bulkCreateWoltSpecials = async (
  menuId: string,
  payload: BulkCreateWoltSpecialsPayload,
): Promise<MenuContainer> =>
  api.post(`/v2/merchant-admin/menus/${menuId}/wolt-specials/batch-create`, payload).then(response => response.data);

export const bulkUpdateAvailabilityPeriodWoltSpecials = async (
  menuId: string,
  payload: BulkUpdateSpecialsPeriodPayload,
): Promise<MenuContainer> =>
  api
    .post(`/v2/merchant-admin/menus/${menuId}/wolt-specials/batch-update/availability`, payload)
    .then(response => response.data);

export const getMenuItemsCommissions = (menuId: string): Promise<ItemCommission[]> =>
  api
    .get<{ commissions: ItemCommission[] }>(`/v2/merchant-admin/menus/${menuId}/custom-item-commissions`)
    .then(response => response.data?.commissions || []);

export const bulkCreateCategory = (menuId: string, data: CategoryCreatePayload[]): Promise<Menu> =>
  api.post<Menu>(`/v2/merchant-admin/menus/${menuId}/batch-categories`, data).then(response => response.data);

export const bulkCreateSubcategory = (
  menuId: string,
  categoryId: string,
  data: SubcategoryCreatePayload[],
): Promise<Menu> =>
  api
    .post<Menu>(`/v2/merchant-admin/menus/${menuId}/categories/${categoryId}/bulk-create-subcategories`, data)
    .then(response => response.data);
